import { useMemo, useState } from 'react';
import { Pin, CircleDashed } from 'lucide-react';
import cn from 'classnames';
import { useMemoizedFn } from 'ahooks';
import { PagePath } from '@/routes/paths';
import { useNavigate, generatePath, useLocation, matchPath } from 'react-router-dom';
import { Button } from '@leadsnavi/ui-kit';
import useRequestsStore from '@/stores/request-session';
import { IApiSessionDto } from '@/services/swagger/easourcing/models';
import { updatePost } from '@/services/swagger/easourcing/SessionService';
import Unread from '@/layouts/main-layout/components/unread';

interface RequestListItemProps {
  data: IApiSessionDto;
  index: number;
}
export default function RequestListItem(props: RequestListItemProps) {
  const { data } = props;
  const [loading, setLoading] = useState(false);
  const { reloadSessionList, unreadStatusMap } = useRequestsStore();
  const navigate = useNavigate();
  const location = useLocation();
  const matchRouter = matchPath(PagePath.chat, location.pathname);

  const isCurrent = useMemo(() => {
    return matchRouter && String(data?.sessionId) === matchRouter?.params?.sessionId;
  }, [data?.sessionId, matchRouter]);

  const isPin = useMemo(() => {
    return data?.pinnedTop === 1;
  }, [data]);

  const handlePinClick = useMemoizedFn(async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();
      if (loading) return;
      setLoading(true);
      await updatePost({
        updateType: 1,
        sessionId: data?.sessionId,
        pinnedTop: isPin ? 0 : 1,
      });
      reloadSessionList();
    } finally {
      setLoading(false);
    }
  });

  const handleNavigateToDetail = useMemoizedFn(() => {
    navigate(generatePath(PagePath.chat, { sessionId: String(data?.sessionId) }));
  });

  return (
    <div
      id={`session-item-${data?.sessionId}`}
      className={cn(
        'min-w-0 rounded-[8px] transition h-9 cursor-pointer flex group items-center px-2 gap-2',
        isCurrent ? 'bg-[#E5E5E5]/65' : 'hover:bg-[#E5E5E5]/45'
      )}
      onClick={handleNavigateToDetail}
    >
      {data?.inquiryTaskRunning ? (
        <CircleDashed color="#D97706" className={cn('w-3 h-3 size-3 animate-[spin_3s_linear_infinite]')} />
      ) : null}
      <div className="flex-1 truncate text-sm text-[#646363]" title={data?.title || ''}>
        {data?.title || '--'}
      </div>
      {isPin && (
        <Button
          variant="ghost"
          className="flex-none w-4.5 h-4.5 rounded-full hover:bg-[#D4D4D4] rotate-45"
          style={{ paddingInline: '0' }}
          disabled={loading}
          onClick={handlePinClick}
        >
          <Pin color="#A3A3A3" fill="#A3A3A3" className={cn('w-3 h-3 size-3')} />
        </Button>
      )}
      <Unread className={cn(!isPin && 'group-hover:hidden')} count={unreadStatusMap?.[data.sessionId] || 0} />
      {!isPin && (
        <Button
          variant="ghost"
          className="flex-none w-4.5 h-4.5 rounded-full hover:bg-[#D4D4D4] hidden group-hover:flex"
          style={{ paddingInline: '0' }}
          disabled={loading}
          onClick={handlePinClick}
        >
          <Pin size={12} color="#262626" className={cn('size-3 w-3 h-3')} />
        </Button>
      )}
    </div>
  );
}
